import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { BehaviorSignalBus } from '../contracts/signals';
import { BehaviorTrackerBase } from './BehaviorTrackerBase';
import { describeElement, getElementFingerprint } from './utils/element';

interface ClickState {
  clickCount: number;
  rageClickCount: number;
  lastClickAt: number;
}

export type ClickTrackerOptions = {
  readonly signalBus?: BehaviorSignalBus;
};

export class ClickTracker extends BehaviorTrackerBase {
  private state: ClickState = {
    clickCount: 0,
    rageClickCount: 0,
    lastClickAt: 0,
  };

  private clickHistory = new Map<string, number[]>();

  private rageCooldownUntil = new Map<string, number>();

  private readonly signalBus: BehaviorSignalBus | undefined;

  constructor(context: PixelRuntimeContext, options?: ClickTrackerOptions) {
    super(context);
    this.signalBus = options?.signalBus;
  }

  protected onInit(): void {
    if (!this.isDocumentAvailable()) return;

    this.resetState();
    this.addDocumentListener(
      'click',
      (event: MouseEvent) => this.handleClick(event),
      true
    );
  }

  protected onDestroy(): void {
    this.clickHistory.clear();
    this.rageCooldownUntil.clear();
  }

  private resetState(): void {
    this.state = {
      clickCount: 0,
      rageClickCount: 0,
      lastClickAt: 0,
    };
    this.clickHistory.clear();
    this.rageCooldownUntil.clear();
  }

  private handleClick(event: MouseEvent): void {
    const now = Date.now();
    const { rageClick } = this.context.config.modules.behavior.trackers.click;

    this.state.clickCount += 1;
    this.state.lastClickAt = now;

    this.signalBus?.publish(
      this.signalBus.createSignal('CLICK', 'ClickTracker', {
        clickCount: this.state.clickCount,
      })
    );

    const target = event.target instanceof Element ? event.target : null;
    const currentTarget =
      event.currentTarget instanceof Element ? event.currentTarget : null;

    const targetDescriptor = describeElement(target);
    const currentTargetDescriptor = describeElement(currentTarget);

    this.dispatchEngagement({
      type: 'PAGE_CLICK',
      category: 'ENGAGEMENT',
      clickCount: this.state.clickCount,
      rageClickCount: this.state.rageClickCount,
      lastClickAt: now,
      target: targetDescriptor,
      currentTarget: currentTargetDescriptor,
      clientX: event.clientX,
      clientY: event.clientY,
      pageX: event.pageX,
      pageY: event.pageY,
      offsetX: event.offsetX,
      offsetY: event.offsetY,
    });

    if (!target || !rageClick.enabled) return;

    const fingerprint = getElementFingerprint(target);

    const cooldownUntil = this.rageCooldownUntil.get(fingerprint) ?? 0;
    if (now < cooldownUntil) return;

    const history = this.clickHistory.get(fingerprint) ?? [];
    const recent = history.filter(ts => now - ts < rageClick.windowMs);
    recent.push(now);
    this.clickHistory.set(fingerprint, recent);

    if (recent.length >= rageClick.threshold) {
      this.state.rageClickCount += 1;
      this.signalBus?.publish(
        this.signalBus.createSignal('RAGE_CLICK', 'ClickTracker', {
          rageClickCount: this.state.rageClickCount,
        })
      );
      this.dispatchEngagement({
        type: 'RAGE_CLICK',
        category: 'ENGAGEMENT',
        threshold: rageClick.threshold,
        windowMs: rageClick.windowMs,
        clickCount: recent.length,
        target: targetDescriptor,
      });
      this.clickHistory.delete(fingerprint);
      this.rageCooldownUntil.set(fingerprint, now + rageClick.windowMs);
    }
  }
}
